/**
 * The true OS version, for the qset-start log event (#1249). Chrome on
 * Android freezes `navigator.userAgent` to `Android 10; K` (the "reduced
 * UA"), so `shortenUserAgent` reports "Android 10" for every Android
 * device regardless of what is actually running -- a live analysis of
 * #1231 read that as "543 records on Android 10", which was an artefact of
 * the freeze, not a fact about the fleet. The real version is only
 * reachable through the User-Agent Client Hints API, which is
 * Chromium-only and async, hence a separate, best-effort read rather than
 * something `shortenUserAgent` itself can do synchronously.
 */

export interface ClientHints {
  platform: string;
  /** Returned exactly as the browser gives it (e.g. "14.0.0") -- never parsed down. */
  platformVersion: string;
  model: string;
}

interface NavigatorUAData {
  /** Low-entropy, readable synchronously -- unlike everything below. */
  platform?: string;
  getHighEntropyValues(hints: string[]): Promise<{
    platform?: string;
    platformVersion?: string;
    model?: string;
  }>;
}

const readUAData = (): NavigatorUAData | undefined =>
  typeof navigator === "undefined"
    ? undefined
    : (navigator as {userAgentData?: NavigatorUAData}).userAgentData;

/**
 * `navigator.userAgentData.platform` is a LOW-entropy hint, so it needs no
 * permission and no promise -- unlike `platformVersion` and `model`. Reading
 * it separately is what guarantees the platform is on every `qset-start`:
 * the high-entropy read can resolve after the event has already been sent,
 * or be refused outright, and then `client-hints` is the only record of it.
 *
 * `undefined` on Safari/Firefox, and on an empty string -- some embedded
 * Chromium webviews send `""`, which in the log would read as a known-blank
 * platform rather than as an unanswered question.
 */
export const readPlatformSync = (): string | undefined =>
  readUAData()?.platform || undefined;

/**
 * `undefined` on Safari/Firefox (no `userAgentData`) or if the call rejects
 * -- never throws. Only Chromium browsers can answer this at all.
 */
export const readClientHints = async (): Promise<ClientHints | undefined> => {
  const uaData = readUAData();
  if (!uaData) return undefined;

  try {
    const values = await uaData.getHighEntropyValues([
      "platform",
      "platformVersion",
      "model",
    ]);
    // Passed through exactly as the browser gives them -- do not parse or
    // normalize platformVersion.
    return values as ClientHints;
  } catch {
    return undefined;
  }
};
